import { Gradient } from '@appcharge/shared-ui';
import { parseFreeTilesLayout } from 'common/buyOneGetManyLayout';
import { OfferUI } from 'common/contracts';
import { EProgressBarType } from 'constants/enums';
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_BORDER_WIDTH,
  POPUP_DEFAULTS,
  PROGRESS_BAR_DEFAULTS,
  PROMOTION_DEFAULTS,
  REWARD_CALENDAR_DEFAULTS,
  ROLLING_OFFER_DEFAULTS,
  TRIGGERED_POPUP_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import {
  InitialValuesBuilder,
  OfferDesignFormValues,
  OfferDesignTypes
} from 'views/OfferDesign/OfferDesign.types';
import {
  formatName,
  sanitizeExternalId
} from 'views/OfferDesign/OfferDesign.utils';

type OfferData = OfferUI | undefined;

type BackgroundColorResolver = (data: OfferData) => Gradient | undefined;

const BACKGROUND_COLOR_RESOLVERS: Partial<
  Record<OfferDesignTypes, BackgroundColorResolver>
> = {
  [OfferDesignTypes.Bundle]: (data) => data?.backgroundColor,
  [OfferDesignTypes.SpecialOffer]: (data) => data?.specialOffer?.backgroundColor,
  [OfferDesignTypes.PopUp]: (data) => data?.specialOffer?.backgroundColor,
  [OfferDesignTypes.TriggeredPopup]: (data) =>
    data?.specialOffer?.backgroundColor,
  [OfferDesignTypes.BuyOneGetMany]: (data) => data?.backgroundColor
};

const buildBackgroundColor = (
  data: OfferData,
  offerType: OfferDesignTypes
): Gradient | undefined => {
  const resolveBackgroundColor = BACKGROUND_COLOR_RESOLVERS[offerType];
  if (resolveBackgroundColor === undefined) {
    return data?.rollingOffer?.backgroundColor;
  }
  return resolveBackgroundColor(data);
};

const buildBaseInitialValues = (
  data: OfferData,
  offerType: OfferDesignTypes,
  isDup: boolean
) => ({
  name: formatName(data?.name, isDup),
  externalId: sanitizeExternalId(data?.externalId),
  backgroundImage: data?.backgroundImage || '',
  backgroundColor:
    buildBackgroundColor(data, offerType) ||
    (offerType === OfferDesignTypes.SpecialOffer ||
    offerType === OfferDesignTypes.PopUp ||
    offerType === OfferDesignTypes.TriggeredPopup
      ? POPUP_DEFAULTS.backgroundColor
      : ''),
  buttonSuffixImage: data?.buttonSuffixImage || '',
  buttonSuffixAnimation: data?.buttonSuffixAnimation || '',
  borderColor: data?.borderColor || DEFAULT_BORDER_COLOR,
  borderWidth: data?.borderWidth ?? DEFAULT_BORDER_WIDTH,
  offerUiType: offerType
});

export const buildPromotionInitialValues: InitialValuesBuilder = (data) => ({
  collectText:
    data?.specialOffer?.collectText || PROMOTION_DEFAULTS.collectText,
  title: data?.specialOffer?.title || '',
  fontSize: data?.specialOffer?.fontSize || PROMOTION_DEFAULTS.fontSize,
  fontWeight: data?.specialOffer?.fontWeight || PROMOTION_DEFAULTS.fontWeight,
  fontColor: data?.specialOffer?.fontColor || PROMOTION_DEFAULTS.fontColor,
  subTitleText: data?.specialOffer?.subTitle?.text || '',
  subTitleFontSize:
    data?.specialOffer?.subTitle?.fontSize || PROMOTION_DEFAULTS.subFontSize,
  subTitleFontWeight:
    data?.specialOffer?.subTitle?.fontWeight || PROMOTION_DEFAULTS.fontWeight,
  subTitleFontColor:
    data?.specialOffer?.subTitle?.fontColor || PROMOTION_DEFAULTS.fontColor
});

const getProgressBarType = (data: OfferData): EProgressBarType => {
  if (data?.progressBar?.type === EProgressBarType.MILESTONE) {
    return EProgressBarType.MILESTONE;
  }
  return EProgressBarType.CLASSIC;
};

const getAccumulatedItemBackgroundImageUrlDefault = (
  progressBarType: EProgressBarType
): string => {
  if (progressBarType === EProgressBarType.MILESTONE) {
    return PROGRESS_BAR_DEFAULTS.pointsBadgeImage;
  }
  return PROGRESS_BAR_DEFAULTS.accumulatedItemBackgroundImageUrl;
};

export const buildProgressBarInitialValues: InitialValuesBuilder = (data) => {
  const progressBarType = getProgressBarType(data);
  const accumulatedItemBackgroundImageUrlDefault =
    getAccumulatedItemBackgroundImageUrlDefault(progressBarType);

  return {
    progressBarType,
    backgroundOpacity:
      data?.progressBar?.backgroundOpacity ??
      PROGRESS_BAR_DEFAULTS.backgroundOpacity,
    frameRadius:
      data?.progressBar?.frameRadius ?? PROGRESS_BAR_DEFAULTS.frameRadius,
    barFillColor:
      data?.progressBar?.barFillColor || PROGRESS_BAR_DEFAULTS.barFillColor,
    barBackgroundColor:
      data?.progressBar?.backgroundColor || PROGRESS_BAR_DEFAULTS.backgroundColor,
    barRadius: data?.progressBar?.barRadius ?? PROGRESS_BAR_DEFAULTS.barRadius,
    borderColor: data?.borderColor || PROGRESS_BAR_DEFAULTS.borderColor,
    borderWidth: data?.borderWidth ?? PROGRESS_BAR_DEFAULTS.borderWidth,
    accumulatedItemImageUrl: data?.progressBar?.accumulatedItem?.imageUrl || '',
    accumulatedItemBackgroundImageUrl:
      data?.progressBar?.accumulatedItem?.backgroundImageUrl ||
      accumulatedItemBackgroundImageUrlDefault,
    textColor: data?.progressBar?.textColor || PROGRESS_BAR_DEFAULTS.textColor,
    barBackgroundImage: data?.progressBar?.barBackgroundImage || '',
    headerImage: data?.progressBar?.headerImage || '',
    title: data?.progressBar?.title?.text || '',
    fontSize:
      data?.progressBar?.title?.fontSize || PROGRESS_BAR_DEFAULTS.titleFontSize,
    fontWeight:
      data?.progressBar?.title?.fontWeight ||
      PROGRESS_BAR_DEFAULTS.titleFontWeight,
    fontColor:
      data?.progressBar?.title?.fontColor || PROGRESS_BAR_DEFAULTS.titleFontColor,
    subTitleText: data?.progressBar?.subtitle?.text || '',
    subTitleFontSize:
      data?.progressBar?.subtitle?.fontSize ||
      PROGRESS_BAR_DEFAULTS.subTitleFontSize,
    subTitleFontWeight:
      data?.progressBar?.subtitle?.fontWeight ||
      PROGRESS_BAR_DEFAULTS.subTitleFontWeight,
    subTitleFontColor:
      data?.progressBar?.subtitle?.fontColor ||
      PROGRESS_BAR_DEFAULTS.subTitleFontColor,
    collectedIconImage:
      data?.progressBar?.collectedIconImage ||
      PROGRESS_BAR_DEFAULTS.collectedIconImage,
    tooltipBackgroundImage: data?.progressBar?.tooltipBackgroundImage || ''
  };
};

export const buildPopupInitialValues: InitialValuesBuilder = (
  data,
  offerSubType
) => ({
  badgeCoverImage: data?.badgeCoverImage || '',
  title: data?.specialOffer?.title || '',
  fontSize: data?.specialOffer?.fontSize || POPUP_DEFAULTS.fontSize,
  fontWeight: data?.specialOffer?.fontWeight || POPUP_DEFAULTS.fontWeight,
  fontColor: data?.specialOffer?.fontColor || POPUP_DEFAULTS.fontColor,
  borderColor: data?.borderColor || POPUP_DEFAULTS.borderColor,
  borderWidth: data?.borderWidth ?? POPUP_DEFAULTS.borderWidth,
  offerUiSubType: data?.offerUiSubType || offerSubType || 'DailyBonus'
});

export const buildTriggeredPopupInitialValues: InitialValuesBuilder = (
  data
) => ({
  badgeCoverImage: data?.badgeCoverImage || '',
  title: data?.specialOffer?.title || '',
  fontSize: data?.specialOffer?.fontSize || TRIGGERED_POPUP_DEFAULTS.fontSize,
  fontWeight:
    data?.specialOffer?.fontWeight || TRIGGERED_POPUP_DEFAULTS.fontWeight,
  fontColor:
    data?.specialOffer?.fontColor || TRIGGERED_POPUP_DEFAULTS.fontColor,
  offerUiSubType: data?.offerUiSubType || 'PostPurchase'
});

export const buildRewardCalendarInitialValues: InitialValuesBuilder = (
  data
) => ({
  backgroundColor:
    data?.rewardCalendar?.backgroundColor ||
    REWARD_CALENDAR_DEFAULTS.backgroundColor,
  dayFontColor:
    data?.rewardCalendar?.dayFontColor || REWARD_CALENDAR_DEFAULTS.dayFontColor,
  headerImage: data?.rewardCalendar?.headerImage || '',
  collectibleBadgeImage:
    data?.rewardCalendar?.collectibleBadgeImage ||
    REWARD_CALENDAR_DEFAULTS.collectibleBadgeImage,
  lockedBadgeImage:
    data?.rewardCalendar?.lockedBadgeImage ||
    REWARD_CALENDAR_DEFAULTS.lockedBadgeImage,
  subRewardBackgroundImage:
    data?.rewardCalendar?.subRewardBackgroundImage || '',
  mainRewardBackgroundImage:
    data?.rewardCalendar?.mainRewardBackgroundImage || '',
  collectIconImage:
    data?.rewardCalendar?.collectIconImage ||
    REWARD_CALENDAR_DEFAULTS.collectIconImage,
  lockedIconImage:
    data?.rewardCalendar?.lockedIconImage ||
    REWARD_CALENDAR_DEFAULTS.lockedIconImage
});

export const buildBuyOneGetManyInitialValues: InitialValuesBuilder = (data) => {
  const onePlusMany = data?.onePlusMany;
  const subOffer = onePlusMany?.subOffer;
  return {
    collectText:
      onePlusMany?.collectText || ROLLING_OFFER_DEFAULTS.collectText,
    headerImage: onePlusMany?.headerImage || '',
    offersBackgroundColor:
      subOffer?.backgroundColor ||
      ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
    offersBackgroundImage: subOffer?.backgroundImage || '',
    freeTilesBackgroundImage: subOffer?.freeTilesBackgroundImage || '',
    offersLockImage: subOffer?.lockImage || ROLLING_OFFER_DEFAULTS.lockImage,
    plusImage: onePlusMany?.plusImage || ROLLING_OFFER_DEFAULTS.plusImage,
    claimedIconImage:
      subOffer?.claimedImage || ROLLING_OFFER_DEFAULTS.claimedIconImage,
    freeTilesLayout: parseFreeTilesLayout(onePlusMany?.freeTilesLayout)
  };
};

export const buildRollingOfferInitialValues: InitialValuesBuilder = (data) => {
  const rollingOffer = data?.rollingOffer;
  const subOffer = rollingOffer?.subRollingOffer;
  return {
    collectText: subOffer?.collectText || ROLLING_OFFER_DEFAULTS.collectText,
    backgroundOpacity:
      rollingOffer?.backgroundOpacity ??
      ROLLING_OFFER_DEFAULTS.backgroundOpacity,
    arrowColor: rollingOffer?.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor,
    arrowImage: rollingOffer?.arrowImage || '',
    headerImage: rollingOffer?.headerImage || '',
    scrollArrowImage: rollingOffer?.scrollArrowImage || '',
    horizontalBackgroundImage: data?.horizontalBackgroundImage || '',
    offersBackgroundColor:
      subOffer?.backgroundColor ||
      ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
    offersBackgroundImage: subOffer?.backgroundImage || '',
    offersLockImage: subOffer?.lockImage || '',
    borderColor: data?.borderColor || ROLLING_OFFER_DEFAULTS.borderColor,
    borderWidth: data?.borderWidth ?? ROLLING_OFFER_DEFAULTS.borderWidth
  };
};

export const buildInitialValues = (
  offerType: OfferDesignTypes,
  data: OfferData,
  isDup: boolean,
  typeBuilder: InitialValuesBuilder | undefined
): OfferDesignFormValues =>
  ({
    ...buildBaseInitialValues(data, offerType, isDup),
    ...typeBuilder?.(data)
  }) as OfferDesignFormValues;
